import {
  ArrowLeftSvg,
  CheckFillSvg,
  color,
  Flex,
  PrimaryButton,
  PrimaryHeading,
  PrimaryText,
  spacing,
  useColorMode,
  useIsMobileView,
} from 'deepstash-ui';
import router from 'next/router';
import React, { useCallback, useMemo } from 'react';
import BackButton from 'src/components/breadcrumbs/BackButton';
import NextLink from 'src/components/navigation/NextLink';
import useAuth from 'src/providers/hooks/useAuth';
import useCollection from 'src/providers/hooks/useCollection';
import useNsi from 'src/providers/hooks/useNsi';
import useProfile from 'src/providers/hooks/useProfile';
import { Analytics, Events } from 'src/services/analytics';
import { CollectionStatus } from 'types/enums';
import {
  getCollectionStatus,
  getNextSourceToReadFromCollectionUrl,
} from 'utils/collection.utils';
import { BLOCK_WIDTH_REM, GAP_SIZE_REM } from 'utils/global';
import actionStrings from 'utils/strings/actionStrings';
import HashtagChipCarousel from '../../../components/hashtag/HashtagChipCarousel';
import NextImage from '../../../components/image/NextImage';
import ProgressBar from './ProgressBar';

const NUMBER_OF_CHIPS_THRESHOLD = 2;

const COMPLETED_CTA_EXTRA_PROPS = {
  _hover: { shadow: 'none', cursor: 'unset' },
  _active: { backgroundColor: color.success },
};

interface CollectionHeaderProps {
  roundedHeaderImage: boolean;
}

const CollectionHeader: React.FC<CollectionHeaderProps> = ({
  roundedHeaderImage,
}) => {
  const { collection } = useCollection();
  const { colorMode } = useColorMode();
  const isMobileView = useIsMobileView();
  const { isLoggedIn } = useAuth();
  const { profile } = useProfile();
  const { nsiDispatch } = useNsi();

  const collectionStatus = collection && getCollectionStatus({ collection });

  const getCta = useMemo(() => {
    if (!collection) return null;

    if (!isLoggedIn) {
      return (
        <PrimaryText
          color={color[colorMode].textInverted}
          size="m"
          type="bold"
          whiteSpace="nowrap"
        >
          {actionStrings.signUpToReadForFree}
        </PrimaryText>
      );
    }

    switch (collectionStatus) {
      case CollectionStatus.STARTED: {
        return (
          <>
            <PrimaryText size="m" type="bold" whiteSpace="nowrap">
              {actionStrings.continueReading}
            </PrimaryText>
            <ArrowLeftSvg
              color={color[colorMode].textInverted}
              boxSize={{ base: spacing.L.rem, lg: spacing.XL.rem }}
              ml={spacing.XS.rem}
              transform="rotate(-180deg)"
            />
          </>
        );
      }
      case CollectionStatus.NEW: {
        return (
          <>
            <PrimaryText
              color={color[colorMode].textInverted}
              size="m"
              type="bold"
              whiteSpace="nowrap"
            >
              {actionStrings.startReading}
            </PrimaryText>
            <ArrowLeftSvg
              color={color[colorMode].textInverted}
              boxSize={{ base: spacing.L.rem, lg: spacing.XL.rem }}
              ml={spacing.XS.rem}
              transform="rotate(-180deg)"
            />
          </>
        );
      }
      case CollectionStatus.COMPLETED: {
        return (
          <>
            <CheckFillSvg
              color={color.light.textInverted}
              mr={spacing.XS.rem}
            />
            <PrimaryText
              color={color.light.textInverted}
              size="m"
              type="bold"
              whiteSpace="nowrap"
            >
              {actionStrings.allRead}
            </PrimaryText>
          </>
        );
      }
      default: {
        return null;
      }
    }
  }, [
    collection,
    collection?.progress.totalReadIdeas,
    collection?.progress.completed,
    profile?.subscriptionType,
    colorMode,
  ]);

  const getCtaBackgroundColor = useMemo(() => {
    if (!collection) return '';

    if (collection?.progress.completed) {
      return color.success;
    } else {
      return color[colorMode].text;
    }
  }, [
    collection,
    collection?.progress.totalReadIdeas,
    collection?.progress.completed,
    profile?.subscriptionType,
    colorMode,
  ]);

  const onClickCtaHandler = useCallback(() => {
    if (!collection) return;
    if (!isLoggedIn) {
      nsiDispatch({
        type: 'openAuth',
        payload: {
          type: 'sign-up',
        },
      });
    } else if (collection?.progress.completed) {
      return;
    } else {
      Analytics.logEvent({
        eventName:
          collection?.progress.totalReadIdeas === 0
            ? Events.consumption.collectionStartReading
            : Events.consumption.collectionContinueReading,
        properties: {
          collectionId: collection?.id,
          location: 'collection-page',
        },
        platforms: ['amplitude'],
      });
      router.push(getNextSourceToReadFromCollectionUrl({ collection }));
    }
  }, [
    collection,
    collection?.progress.totalReadIdeas,
    collection?.progress.completed,
    profile?.subscriptionType,
  ]);

  if (!collection) return null;

  return (
    <>
      <Flex
        pos="relative"
        flexDirection="column"
        backgroundColor={color[colorMode].background}
      >
        <Flex
          align="center"
          cursor="pointer"
          width="100%"
          justifyContent="space-between"
          mb={spacing.M.rem}
        >
          <BackButton location="collection" />
          <NextLink href="/all-collections">
            <PrimaryText color={color[colorMode].primary} fontWeight="bold">
              {actionStrings.seeAllCollections}
            </PrimaryText>
          </NextLink>
        </Flex>
      </Flex>
      <Flex
        flexDir="column"
        zIndex={2}
        backgroundColor={color[colorMode].background}
      >
        <Flex
          alignItems="center"
          mb={GAP_SIZE_REM}
          flexDirection={{ base: 'column', md: 'row' }}
        >
          <Flex
            position="relative"
            alignItems="center"
            justifyContent="center"
            height={spacing.toRem(128)}
            width={isMobileView ? '100%' : spacing.toRem(212)}
          >
            <NextImage
              wrapperStyle={{
                h: spacing.toRem(128),
                w: isMobileView ? '100%' : spacing.toRem(212),
                borderRadius: roundedHeaderImage ? spacing.M.rem : 0,
                cursor: 'initial',
                transformOrigin: 'center',
              }}
              priority
              imageUrl={collection.imageUrl}
              blurhash={collection.imageBlurhash}
            />
            <Flex
              position="absolute"
              left={0}
              right={0}
              top={0}
              bottom={0}
              margin="auto"
              style={{
                fontSize: '2.5rem',
                width: BLOCK_WIDTH_REM,
                height: BLOCK_WIDTH_REM,
                alignItems: 'center',
                transformOrigin: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                backgroundColor: color[colorMode].top,
                borderRadius: BLOCK_WIDTH_REM,
              }}
              zIndex={2}
            >
              {collection.emoji}
            </Flex>
          </Flex>
          <Flex
            flexDirection="column"
            height="auto"
            justifyContent="center"
            mt={{ base: '1rem', md: 0 }}
            ml={{ base: '0rem', md: '2rem' }}
          >
            <PrimaryHeading
              size="h1"
              textSize="xl"
              color={color[colorMode].text}
              lineHeight="2.25rem"
              fontWeight={700}
              fontSize="2rem"
              noOfLines={4}
            >
              {collection.name}
            </PrimaryHeading>
            <PrimaryText
              color={color[colorMode].text}
              size={isMobileView ? 's' : 'm'}
              mt={spacing.XS.rem}
              display={'flex'}
            >
              {collection.description ?? ''}
            </PrimaryText>
          </Flex>
        </Flex>

        <Flex alignItems="center" flexDirection={{ base: 'column', md: 'row' }}>
          <PrimaryButton
            size="lg"
            width={{ base: 'calc(100%)', md: spacing.toRem(212) }}
            px={{
              base: spacing.M.rem,
              lg: spacing.toRem(48),
            }}
            height={spacing.toRem(54)}
            backgroundColor={getCtaBackgroundColor}
            onClick={onClickCtaHandler}
            {...(collectionStatus === CollectionStatus.COMPLETED
              ? COMPLETED_CTA_EXTRA_PROPS
              : {})}
            overflow="hidden"
            border={0}
            _hover={undefined}
          >
            <Flex align="center">{getCta}</Flex>
            {collection.progress.totalReadIdeas > 0 && (
              <ProgressBar
                currentValue={collection.progress.totalReadIdeas}
                totalValue={collection.ideaCount}
                flexGrow={1}
                left={0}
                width="100%"
                bottom={0}
                height={spacing.toRem(4)}
                borderRadius={spacing.toRem(4)}
                position="absolute"
                bgColor={color[colorMode].primary.darker}
                isPadded
              />
            )}
          </PrimaryButton>
          <HashtagChipCarousel
            hashtags={collection.hashtags.map(hashtag => ({ name: hashtag }))}
            numberOfChipsThreshold={NUMBER_OF_CHIPS_THRESHOLD}
            color={color[colorMode].textSecondary}
            ml={{ base: 0, md: GAP_SIZE_REM }}
            mt={{ base: GAP_SIZE_REM, md: 0 }}
          />
        </Flex>
      </Flex>
    </>
  );
};

export default CollectionHeader;
